import type {IconProps} from '@lightricks/react-design-system/dist/components/icon/IconProps';
import translate from '@/utils/translate';
import {
  CampaignMetricsTotalsByNetwork,
  CampaignMetricsAveragesByNetwork,
  SocialStreamTab,
} from '@/types/socialMediaPost';

export interface StatConfig {
  key: string;
  label: string;
  iconName: IconProps['name'];
  getValue: (
    stats: CampaignMetricsTotalsByNetwork | CampaignMetricsAveragesByNetwork
  ) => number | undefined;
  format?: 'number' | 'percentage';
}

function getStatIconName(key: string): IconProps['name'] {
  const normalizedKey = key.replace(/PerPost$/, '');

  switch (normalizedKey) {
    case 'creatorCount':
    case 'followerCount':
      return 'Navigation-Profile';
    case 'impressionCount':
      return 'Features-Eye';
    case 'commentCount':
    case 'replyCount':
      return 'Actions-Comment1';
    case 'likeCount':
      return 'Actions-Like';
    case 'videoAggregatedPlaysCount':
    case 'viewCount':
      return 'Features-Play';
    case 'totalInteractionsCount':
    case 'reachCount':
      return 'Features-Trending';
    default:
      return 'Features-Trending';
  }
}

function createTotalStatConfig(
  key: keyof CampaignMetricsTotalsByNetwork,
  labelKey: string,
  format?: 'number' | 'percentage',
  getValue?: (
    stats: CampaignMetricsTotalsByNetwork | CampaignMetricsAveragesByNetwork
  ) => number | undefined
): StatConfig {
  return {
    key,
    label: translate(labelKey),
    iconName: getStatIconName(key),
    getValue: getValue || ((s) => (s as CampaignMetricsTotalsByNetwork)[key]),
    format,
  };
}

function createAverageStatConfig(
  key: keyof CampaignMetricsAveragesByNetwork,
  labelKey: string,
  format?: 'number' | 'percentage',
  getValue?: (
    stats: CampaignMetricsAveragesByNetwork | CampaignMetricsTotalsByNetwork
  ) => number | undefined
): StatConfig {
  return {
    key,
    label: translate(labelKey),
    iconName: getStatIconName(key),
    getValue: getValue || ((s) => (s as CampaignMetricsAveragesByNetwork)[key]),
    format,
  };
}

function isTikTok(network?: string | null): boolean {
  return network === 'tiktok_v2';
}

// TikTok's backend stores totalInteractionsCount as 0 (saves data is
// blocked), so derive Total Interactions from the fields it does return.
function tikTokTotalInteractionsTotal(
  stats: CampaignMetricsTotalsByNetwork
): number {
  return (
    (stats.likeCount ?? 0) + (stats.commentCount ?? 0) + (stats.shareCount ?? 0)
  );
}

function tikTokTotalInteractionsAverage(
  stats: CampaignMetricsAveragesByNetwork
): number {
  return (
    (stats.likeCountPerPost ?? 0) +
    (stats.commentCountPerPost ?? 0) +
    (stats.shareCountPerPost ?? 0)
  );
}

export function getTotalStatsForStream(
  socialStream: SocialStreamTab,
  network?: string | null
): StatConfig[] {
  const reachCount = createTotalStatConfig('reachCount', 'Reach');
  const totalInteractionsCount = isTikTok(network)
    ? createTotalStatConfig(
        'totalInteractionsCount',
        'Total Interactions',
        undefined,
        (s) => tikTokTotalInteractionsTotal(s as CampaignMetricsTotalsByNetwork)
      )
    : createTotalStatConfig('totalInteractionsCount', 'Total Interactions');
  const creatorCount = createTotalStatConfig('creatorCount', 'Creators');
  const followerCount = createTotalStatConfig('followerCount', 'Followers');
  // TikTok has no distinct "plays" metric (videoAggregatedPlaysCount is 0), so
  // relabel to Views sourced from viewCount.
  const playsCount = isTikTok(network)
    ? createTotalStatConfig('viewCount', 'Views')
    : createTotalStatConfig('videoAggregatedPlaysCount', 'Plays');
  const impressionCount = createTotalStatConfig(
    'impressionCount',
    'Impressions'
  );
  const replyCount = createTotalStatConfig('replyCount', 'Replies');

  // TikTok uses this single card set for every tab; only the data sources
  // (handled above) differ from Instagram's.
  if (isTikTok(network)) {
    return [totalInteractionsCount, creatorCount, followerCount, playsCount];
  }

  const streamSpecificStats: Record<SocialStreamTab, StatConfig[]> = {
    feed: [creatorCount, followerCount, impressionCount],
    reels: [totalInteractionsCount, creatorCount, followerCount, playsCount],
    story: [impressionCount, reachCount, replyCount],
    all: [totalInteractionsCount, creatorCount, followerCount, playsCount],
  };

  return streamSpecificStats[socialStream];
}

export function getAverageStatsForStream(
  socialStream: SocialStreamTab,
  network?: string | null
): StatConfig[] {
  const followerCountPerPost = createAverageStatConfig(
    'followerCountPerPost',
    'Followers'
  );
  const commentCountPerPost = createAverageStatConfig(
    'commentCountPerPost',
    'Comments'
  );
  const likeCountPerPost = createAverageStatConfig('likeCountPerPost', 'Likes');
  const reachCountPerPost = createAverageStatConfig(
    'reachCountPerPost',
    'Reach'
  );
  const videoAggregatedPlaysCountPerPost = isTikTok(network)
    ? createAverageStatConfig('viewCountPerPost', 'Views')
    : createAverageStatConfig('videoAggregatedPlaysCountPerPost', 'Plays');
  const impressionCountPerPost = createAverageStatConfig(
    'impressionCountPerPost',
    'Impressions'
  );
  // Backend doesn't populate totalInteractionsCountPerPost for TikTok.
  const totalInteractionsCountPerPost = isTikTok(network)
    ? createAverageStatConfig(
        'totalInteractionsCountPerPost',
        'Total Interactions',
        undefined,
        (s) =>
          tikTokTotalInteractionsAverage(s as CampaignMetricsAveragesByNetwork)
      )
    : createAverageStatConfig(
        'totalInteractionsCountPerPost',
        'Total Interactions'
      );
  const replyCountPerPost = createAverageStatConfig(
    'replyCountPerPost',
    'Replies'
  );

  // TikTok uses this single card set for every tab; only the data sources
  // (handled above) differ from Instagram's.
  if (isTikTok(network)) {
    return [
      totalInteractionsCountPerPost,
      followerCountPerPost,
      videoAggregatedPlaysCountPerPost,
    ];
  }

  const streamSpecificStats: Record<SocialStreamTab, StatConfig[]> = {
    feed: [likeCountPerPost, commentCountPerPost],
    reels: [
      totalInteractionsCountPerPost,
      followerCountPerPost,
      videoAggregatedPlaysCountPerPost,
    ],
    story: [impressionCountPerPost, reachCountPerPost, replyCountPerPost],
    all: [
      totalInteractionsCountPerPost,
      followerCountPerPost,
      videoAggregatedPlaysCountPerPost,
    ],
  };

  return streamSpecificStats[socialStream];
}
